/**
 * NotificationBell — In-app notification icon with dropdown panel.
 * Polls every 30s, shows unread badge, grouped by type.
 */
import React, { useState, useEffect, useCallback, useRef, useSyncExternalStore } from 'react';
import { Bell, AlertTriangle, X, Check } from 'lucide-react';
import { useNavigate } from 'react-router';
import {
  clearNotifications,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/api/notifications';
import { isDesktopOffline } from '@/api/desktopSync';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { confirmCommunityNavigation } from '@/utils/communityNavigationGuard';

const TYPE_CONFIG = {
  delivery_failed: {
    icon: AlertTriangle,
    color: 'text-red-500',
    bg: 'bg-red-50',
    border: 'border-red-100',
    navigateTo: '/dispatch',
    tabHint: 'log',
  },
};

const DEFAULT_TYPE = {
  icon: Bell,
  color: 'text-stone-500',
  bg: 'bg-stone-50',
  border: 'border-stone-100',
  navigateTo: '/',
};

function timeAgo(isoStr) {
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const NotificationBellSession = ({ binding, getToken, enabled }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loadState, setLoadState] = useState('loading');
  const [pending, setPending] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [notice, setNotice] = useState('');
  const panelRef = useRef(null);
  const mounted = useRef(false);
  const read = useRef(null);
  const write = useRef(null);
  const mustRefresh = useRef(false);
  const owns = useCallback(() => mounted.current && enabled
    && binding.revision === getActiveSessionRevision() && !isDesktopOffline(), [binding, enabled]);

  const invalidateRead = useCallback(() => {
    read.current?.abort();
    read.current = null;
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!owns() || read.current || write.current) return;
    const token = getToken?.();
    if (!token || !owns()) return;
    const controller = new AbortController();
    read.current = controller;
    try {
      const data = await getNotifications({
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal, timeout: 15000,
        suppressServerErrorNotification: true,
      });
      if (!owns() || read.current !== controller) return;
      if (!Array.isArray(data?.notifications) || !Number.isInteger(data?.unread_count)
          || data.unread_count < 0 || data.notifications.some(n => !n || typeof n.id !== 'string')) {
        throw new Error('Invalid notification response');
      }
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
      setLoadState('ready');
      mustRefresh.current = false;
      setNeedsRefresh(false);
    } catch {
      if (owns() && read.current === controller) setLoadState('error');
    } finally {
      if (read.current === controller) read.current = null;
    }
  }, [owns, getToken]);

  // TCA-26101405: one mounted session owns reads; a write retires older polls.
  useEffect(() => {
    mounted.current = true;
    fetchNotifications();
    const interval = enabled ? setInterval(fetchNotifications, 30000) : null;
    return () => {
      mounted.current = false;
      clearInterval(interval);
      invalidateRead();
    };
  }, [fetchNotifications, invalidateRead, enabled]);

  // Close panel on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const mutate = async (kind, id) => {
    if (!owns() || write.current || mustRefresh.current || loadState !== 'ready') return;
    if (kind === 'read' && !notifications.some(n => n.id === id && !n.read)) return;
    const token = getToken?.();
    if (!token || !owns()) return;
    const attempt = {};
    write.current = attempt;
    mustRefresh.current = true;
    setNeedsRefresh(true);
    setPending(true);
    setNotice('');
    invalidateRead();
    const config = { headers: { Authorization: `Bearer ${token}` }, timeout: 15000,
      suppressServerErrorNotification: true };
    try {
      if (kind === 'read') await markNotificationRead(id, config);
      else if (kind === 'all') await markAllNotificationsRead(config);
      else await clearNotifications(config);
      if (!owns()) return;
      const next = kind === 'clear' ? [] : notifications.map(n => (
        kind === 'all' || n.id === id ? { ...n, read: true } : n
      ));
      setNotifications(next);
      setUnreadCount(next.filter(n => !n.read).length);
      setNotice('Change saved. Refreshing notifications does not repeat it.');
    } catch (error) {
      if (!owns()) return;
      const status = error?.response?.status;
      setNotice(!status || status === 408 || status >= 500
        ? 'Result not confirmed; the action may have succeeded. Refresh and review the current list before choosing another action.'
        : 'The notification action was not accepted. Refresh the list before trying again.');
    } finally {
      if (write.current === attempt) write.current = null;
      if (owns()) {
        setPending(false);
        await fetchNotifications();
      }
    }
  };
  const markRead = id => mutate('read', id);
  const blocked = !enabled || pending || needsRefresh || loadState !== 'ready';

  const handleNotificationClick = (notification) => {
    if (!owns()) return;
    if (!confirmCommunityNavigation()) return;
    if (!notification.read) markRead(notification.id);
    const config = TYPE_CONFIG[notification.type] || DEFAULT_TYPE;
    setIsOpen(false);
    navigate(config.navigateTo);
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        type="button"
        disabled={!enabled}
        aria-label="Notifications"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center p-2 rounded-xl text-stone-400 hover:bg-stone-100 hover:text-stone-700 transition-all duration-200"
        data-testid="notification-bell"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full px-1"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-80 bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden z-[100]"
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
            <h3 className="text-sm font-semibold text-stone-900">Notifications</h3>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button
                  onClick={() => mutate('all')}
                  disabled={blocked}
                  className="text-xs text-primary hover:underline"
                  data-testid="mark-all-read-btn"
                >
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={() => mutate('clear')}
                  disabled={blocked}
                  className="text-xs text-stone-400 hover:text-stone-600 hover:underline"
                  data-testid="clear-all-btn"
                >
                  Clear all
                </button>
              )}
              <button aria-label="Close notifications" onClick={() => setIsOpen(false)} className="text-stone-400 hover:text-stone-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div aria-live="polite" className="text-xs text-stone-600">
            {pending && <p className="px-4 py-2">Updating notifications…</p>}
            {notice && <p className="px-4 py-2" data-testid="notification-notice">{notice}</p>}
            {loadState === 'loading' && <p className="px-4 py-2">Loading notifications…</p>}
            {loadState === 'error' && (
              <div className="px-4 py-2" role="status">
                <p>Could not refresh notifications. {notifications.length > 0 ? 'Shown notifications may be out of date.' : 'The current list is unavailable.'}</p>
                <button onClick={fetchNotifications} disabled={pending} className="text-primary underline" data-testid="notification-refresh-btn">Retry refresh only</button>
              </div>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 && loadState === 'ready' && !needsRefresh ? (
              <div className="py-8 text-center text-sm text-stone-400">
                <Bell className="mx-auto mb-2 h-4 w-4" />
                No notifications
              </div>
            ) : (
              notifications.map((n) => {
                const config = TYPE_CONFIG[n.type] || DEFAULT_TYPE;
                const Icon = config.icon;
                return (
                  <div
                    key={n.id}
                    className={`w-full text-left px-4 py-3 border-b border-stone-50 hover:bg-stone-50 transition-colors flex gap-3 ${
                      !n.read ? 'bg-blue-50/30' : ''
                    }`}
                  >
                    <div className={`flex-shrink-0 p-1.5 rounded-lg ${config.bg} ${config.border} border`}>
                      <Icon className={`h-3.5 w-3.5 ${config.color}`} />
                    </div>
                    <button type="button" onClick={() => handleNotificationClick(n)} className="flex-1 min-w-0 text-left" data-testid={`notification-item-${n.id}`}>
                      <span className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-stone-800">{n.title}</span>
                        {!n.read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                        )}
                      </span>
                      <span className="block text-xs text-stone-500 mt-0.5 truncate">{n.message}</span>
                      <span className="text-[10px] text-stone-400 mt-1 block">{timeAgo(n.created_at)}</span>
                    </button>
                    {!n.read && (
                      <button
                        onClick={() => markRead(n.id)}
                        disabled={blocked}
                        data-testid={`notification-read-${n.id}`}
                        aria-label={`Mark ${n.title} as read`}
                        className="flex-shrink-0 p-1 rounded hover:bg-stone-100 text-stone-300 hover:text-stone-500"
                        title="Mark as read"
                      >
                        <Check className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const NotificationBell = () => {
  const { sessionBinding, getToken } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const enabled = !!sessionBinding?.user?.id && !!sessionBinding?.user?.tenant_id
    && sessionBinding.source === 'cloud' && sessionBinding.revision === revision && !isDesktopOffline();
  const identity = JSON.stringify([sessionBinding?.user?.tenant_id, sessionBinding?.user?.id,
    sessionBinding?.source, sessionBinding?.revision, revision, enabled]);
  return <NotificationBellSession key={identity} binding={sessionBinding} getToken={getToken} enabled={enabled} />;
};

export default NotificationBell;
